import { SettingsIcon, WifiIcon } from "@xola/icons";
import { Alert, Button } from "@xola/ui-kit";
import clsx from "clsx";
import { isEqual } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { Event, Experience, ExperiencePurchaseItem, Permission, Timeslot } from "types";
import { useResource } from "../../../hooks/useResource";
import { useDevice } from "../../../hooks/useSystemInfo";
import { useVisibility } from "../../../hooks/useVisibility";
import { canStartVirtualMeeting } from "../../../models/Event";
import { isVirtualExperience } from "../../../models/Experience";
import { BannerFallback, EventBanner } from "../../Common/EventBanner";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ErrorBoundary } from "../../ErrorBoundary";
import { CreateEventNoteModal } from "../../Event/CreateEventNoteModal";
import { EventActionsMenu } from "../Common/EventActionsMenu";
import { VirtualMeetingUrlsModal } from "../VirtualMeetingUrlsModal";
import { RosterGuestList } from "./RosterGuestList";
import { EventNoteSection } from "./RosterGuestList/EventNoteSection";
import { useRosterGuestListForPrint } from "./useRosterGuestListForPrint";
import { DashboardRosterWaitlist } from "./Waitlist/DashboardRosterWaitlist";
import { UnassignGuidePromptHost } from "@/components/Dashboard/Resources/Guides/UnassignGuidePromptHost";
import { EmbeddedAppTriggerLocation } from "@/embeddedApps/EmbeddedAppTriggerLocation";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

interface DashboardRosterProps {
    timeslot: Timeslot;
}

export const DashboardRoster = ({ timeslot }: DashboardRosterProps) => {
    const eventBannerRef = useRef<HTMLDivElement>(null);

    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);
    const { data: experience } = useResource<Experience>(["products", timeslot.product]);

    const { data: purchaseItems } = useAutoFetchCollection<ExperiencePurchaseItem>(
        "purchaseItems",
        { event: timeslot.event?.id, expand: "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule" },
        { enabled: !!timeslot.event?.id },
    );

    // Background polling of purchaseItems hands back a fresh array reference each tick even when
    // the data is unchanged. Cache the built context by content so a no-op poll reuses the same
    // object and consumers relying on identity (memoized children, effect deps) don't re-fire.
    const rosterContextRef = useRef<{ events: unknown[] }>();

    const rosterContext = useMemo(() => {
        // Without an event id there is no valid roster context to hand an app; emit an empty list
        // rather than an event entry with an undefined id.
        if (!timeslot.event?.id) {
            return { events: [] };
        }
        // purchaseId -> (itemId -> guestId set), preserving the full event > purchase > item > guest hierarchy
        const purchaseMap = new Map<string, Map<string, Set<string>>>();
        for (const item of purchaseItems) {
            let itemMap = purchaseMap.get(item.purchase.id);
            if (!itemMap) {
                itemMap = new Map();
                purchaseMap.set(item.purchase.id, itemMap);
            }
            let guestIds = itemMap.get(item.id);
            if (!guestIds) {
                guestIds = new Set();
                itemMap.set(item.id, guestIds);
            }
            for (const g of item.guests) {
                guestIds.add(g.id);
            }
        }
        const nextContext = {
            events: [
                {
                    id: timeslot.event?.id,
                    purchases: [...purchaseMap.entries()].map(([purchaseId, itemMap]) => ({
                        id: purchaseId,
                        items: [...itemMap.entries()].map(([itemId, guestIds]) => ({
                            id: itemId,
                            guests: [...guestIds].map((gid) => ({ id: gid })),
                        })),
                    })),
                },
            ],
        };

        const cached = rosterContextRef.current;
        if (cached && isEqual(nextContext, cached)) {
            return cached;
        }
        rosterContextRef.current = nextContext;
        return nextContext;
    }, [timeslot.event?.id, purchaseItems]);

    const [showAddNotesModal, setShowAddNotesModal] = useState(false);
    const isVirtual = (!!event?.virtualMeeting || isVirtualExperience(experience)) ?? false;
    const [showVirtualMeetingUrlsModal, { toggle: toggleVirtualMeetingUrlsModal }] = useVisibility(false);
    const showStartVirtualMeeting = canStartVirtualMeeting(event, experience);
    const { isPrintRosterGuestListVisible, showPrintRosterGuestList } = useRosterGuestListForPrint();

    const { isTabletOrMobile } = useDevice();

    const toggleNotesModal = () => {
        setShowAddNotesModal(!showAddNotesModal);
    };

    const handleStartVirtualExperience = () => {
        const sellerUrl = event?.virtualMeeting.sellerUrl;
        if (sellerUrl) {
            window.open(sellerUrl);
        }
    };

    return (
        <div
            className={clsx(
                "ui-column-layout flex flex-col",
                isTabletOrMobile ? "h-[700px] max-h-[700px]" : "h-screen max-h-screen",
            )}
            data-testid="dashboard-roster"
        >
            <div className="ui-product-banner space-y-3 bg-white">
                <ErrorBoundary fallbackComponent={<BannerFallback timeslot={timeslot} />}>
                    {experience ? (
                        <EventBanner
                            innerRef={eventBannerRef}
                            product={experience ?? { id: timeslot.product }}
                            timeslot={timeslot}
                            isVirtual={isVirtual}
                        />
                    ) : null}
                    {isVirtual ? (
                        <Alert color="primary" className="w-full">
                            <div className="flex items-center justify-between p-1">
                                <div className="flex space-x-4">
                                    <WifiIcon size="medium" />
                                    <div className="text-lg font-bold">Online Experience</div>
                                </div>
                                <div className="flex">
                                    {event && (
                                        <SecondaryButton size="small" onClick={toggleVirtualMeetingUrlsModal}>
                                            <SettingsIcon />
                                        </SecondaryButton>
                                    )}
                                    {showStartVirtualMeeting ? (
                                        <Button
                                            icon={<WifiIcon />}
                                            className="ml-3"
                                            size="small"
                                            onClick={handleStartVirtualExperience}
                                        >
                                            Start Online Experience
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        </Alert>
                    ) : null}
                    <EventActionsMenu
                        isDashboardRosterView
                        timeslot={timeslot}
                        className="flex h-14 w-full items-center justify-between"
                        onPrintRosterClick={showPrintRosterGuestList}
                    >
                        <EmbeddedAppTriggerLocation id="dashboard.roster.headerBar" context={rosterContext} />
                    </EventActionsMenu>
                    <VirtualMeetingUrlsModal
                        isOpen={showVirtualMeetingUrlsModal}
                        event={event}
                        onClose={toggleVirtualMeetingUrlsModal}
                    />
                </ErrorBoundary>
            </div>

            {/* To have the possibility to set the roster table to 100% available space we need to set the height of the parent element as well. */}
            {/* Changing height here will affect the roster ag-grid table too. */}
            <div className="h-full space-y-12 overflow-y-visible pt-6 pb-10">
                {event ? (
                    <ErrorBoundary fallbackMessage="Error rendering event notes">
                        <EventNoteSection event={event} heading="Event Notes" />
                    </ErrorBoundary>
                ) : null}
                {event && experience ? (
                    <ErrorBoundary fallbackMessage="Error rendering roster guests">
                        <RosterGuestList
                            eventBannerRef={eventBannerRef}
                            timeslot={timeslot}
                            experience={experience}
                            isPrintRosterGuestListVisible={isPrintRosterGuestListVisible}
                        />
                    </ErrorBoundary>
                ) : null}
                <IsGranted permissions={[Permission.WAITLISTS_VIEW]}>
                    <ErrorBoundary fallbackMessage="Error rendering waitlist information">
                        <DashboardRosterWaitlist timeslot={timeslot} />
                    </ErrorBoundary>
                </IsGranted>
                {event ? (
                    <CreateEventNoteModal events={[event]} isOpen={showAddNotesModal} onClose={toggleNotesModal} />
                ) : null}
                <UnassignGuidePromptHost />
            </div>
        </div>
    );
};
